import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { compressImage, CompressImageError, CompressImageOptions } from 'shared/utils/image/compress-image';
import {
  describeCompression,
  describeImageFailure,
  describeRejection,
  FileDescription,
  isHeicFile,
} from 'shared/utils/image/image-messages';

/**
 * Обработка выбранных файлов перед тем, как они попадут в форму.
 *
 * Живёт в ките, а не в конкретной форме, потому что ограничение общее для всех
 * загрузок: бэкенд изображения не сжимает, а nginx рубит запрос больше 5 МБ
 * HTML-страницей 413 — телефонное фото без обработки не доходит никуда, ни в
 * заявку, ни в карточку игрока, ни в логотип команды.
 *
 * Единственный канал обратной связи здесь — тост: файл подменяется молча, и без
 * сообщения человек не узнает ни что изображение сжали, ни почему его не приняли.
 */

/** `false` — грузить как есть; объект — свои пороги вместо умолчаний. */
export type ImageCompression = boolean | CompressImageOptions;

/** Файл, который не приняла сама дропзона, с кодами ошибок `react-dropzone`. */
export interface RejectedFile {
  file: FileDescription;
  codes: string[];
}

export function useImageCompression(compression: ImageCompression = true) {
  const [isProcessing, setIsProcessing] = useState(false);

  const processFiles = useCallback(
    async (accepted: File[], rejected: RejectedFile[] = []): Promise<File[]> => {
      // Про отказ говорим один раз: при загрузке пачки список тостов на каждый
      // файл заслонил бы саму форму, а причина у них обычно общая.
      if (rejected.length > 0) {
        const [first] = rejected;
        toast.error(describeRejection(first.file, first.codes));
      }

      if (accepted.length === 0) return [];
      if (compression === false) return accepted;

      const options: CompressImageOptions | undefined = compression === true ? undefined : compression;

      setIsProcessing(true);
      try {
        const processed: File[] = [];

        // Последовательно, а не Promise.all: локации грузят до пяти фото сразу, и
        // параллельное декодирование держало бы в памяти пять полноразмерных битмапов.
        for (const source of accepted) {
          // HEIC отсекаем до чтения в память: браузер его не декодирует, и тратить
          // время на многомегабайтный файл, чтобы упасть, незачем.
          if (isHeicFile(source)) {
            toast.error(describeImageFailure(new CompressImageError('decode-failed'), source));
            continue;
          }

          try {
            const result = await compressImage(source, options);
            processed.push(result.file);

            const message = describeCompression(result);
            if (message) toast.info(message);
          } catch (error) {
            // Сбойный файл выпадает из результата, остальные продолжают идти:
            // из пяти фото локации не должно теряться четыре из-за одного битого.
            toast.error(describeImageFailure(error, source));
          }
        }

        return processed;
      } finally {
        setIsProcessing(false);
      }
    },
    [compression],
  );

  return { processFiles, isProcessing };
}
